import { isImageUrl } from "@/utils/urlUtils";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React, { useState } from "react";
import RegularVideoCard from "./RegularVideoCard";
import SingleImageWithFallback from "./SingleImageWithFallback";

type SocialMediaCarouselProps = {
  mediaUrls: string[];
  className?: string;
};

const SocialMediaCarousel: React.FC<SocialMediaCarouselProps> = ({
  mediaUrls,
  className,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    const isFirstSlide = currentIndex === 0;
    const newIndex = isFirstSlide ? mediaUrls.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastSlide = currentIndex === mediaUrls.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  const goToSlide = (slideIndex: number) => {
    setCurrentIndex(slideIndex);
  };

  if (!mediaUrls || mediaUrls.length === 0) {
    return null; // Don't render anything if no media
  }

  return (
    <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
      {isImageUrl(mediaUrls[currentIndex]) ? (
        <SingleImageWithFallback url={mediaUrls[currentIndex]} />
      ) : (
        <RegularVideoCard url={mediaUrls[currentIndex]} />
      )}

      {/* Navigation Buttons (Always visible, conditional rendering) */}
      {currentIndex > 0 && (
        <Button
          shape="circle"
          size="small"
          icon={<LeftOutlined />}
          onClick={goToPrevious}
          className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white hover:bg-gray-200 text-black border-none opacity-50 hover:opacity-100 transition-all duration-300"
          aria-label="Previous slide"
        />
      )}
      {currentIndex < mediaUrls.length - 1 && (
        <Button
          shape="circle"
          size="small"
          icon={<RightOutlined />}
          onClick={goToNext}
          className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white hover:bg-gray-200 text-black border-none opacity-50 hover:opacity-100 transition-all duration-300"
          aria-label="Next slide"
        />
      )}

      {/* Slide Indicator Dots */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1.5">
        {mediaUrls.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-1.5 w-1.5 rounded-full focus:outline-none transition-colors duration-300 ${
              currentIndex === index
                ? "bg-white"
                : "bg-gray-500 hover:bg-gray-400"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default SocialMediaCarousel;
